import React, { useRef, useState } from "react";
import api from "../../services/api";
import { FlashMessage } from "../assets/utils";
import './BarProgression.css';

const BarProgression = ({ book, setBook }) => {
    const [value, setValue] = useState(book.progression ?? 0);
    const [dragging, setDragging] = useState(false);
    const [flashMessage, setFlashMessage] = useState(null);
    const sliderRef = useRef(null);

    const isFinished = value === 100;

    const handleChange = (e) => {
        setValue(parseInt(e.target.value, 10));
    };

    const handleCommit = async (e) => {
        const newValue = parseInt(e.target.value, 10);
        setValue(newValue);
        setDragging(false);
        setBook(prev => ({ ...prev, progression: newValue }));
        try {
            await api.post('/updateProgression', { progression: newValue, bookId: book.book.id });
            setFlashMessage(newValue === 100 ? '🎉 Book finished!' : 'Progress saved');
        } catch (err) {
            console.error('Failed to update progression', err);
        }
    };

    return (
        <div>
            {flashMessage && (
                <FlashMessage message={flashMessage} duration={2000} onClose={() => setFlashMessage(null)} />
            )}

            <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-warm-500 uppercase tracking-wide">Reading progress</p>
                <span className={`text-sm font-bold ${isFinished ? 'text-amber-500' : 'text-warm-700'}`}>
                    {isFinished ? '✓ Finished' : `${value}%`}
                </span>
            </div>

            {/* Track with filled amber overlay */}
            <div className="relative mb-3">
                <div className="w-full h-1.5 bg-warm-100 rounded-full overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all duration-200 ${isFinished ? 'bg-amber-400' : 'bg-amber-400'}`}
                        style={{ width: `${value}%` }}
                    />
                </div>
                <input
                    ref={sliderRef}
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={value}
                    onChange={handleChange}
                    onMouseDown={() => setDragging(true)}
                    onMouseUp={handleCommit}
                    onTouchEnd={handleCommit}
                    className="absolute inset-0 opacity-0 w-full cursor-pointer"
                    style={{ height: '6px', top: '-2px' }}
                />
                {/* Visible thumb */}
                <div
                    className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white shadow-md transition-all pointer-events-none
                        ${isFinished ? 'bg-amber-400' : 'bg-warm-600'} ${dragging ? 'scale-125' : ''}`}
                    style={{ left: `calc(${value}% - 8px)` }}
                />
            </div>

            {/* Quick-set buttons */}
            <div className="flex gap-2 mt-3">
                {[25, 50, 75, 100].map((preset) => (
                    <button
                        key={preset}
                        onClick={() => handleCommit({ target: { value: preset } })}
                        className={`flex-1 py-1 text-xs font-medium rounded-lg border transition-colors
                            ${value === preset
                                ? 'bg-warm-600 text-white border-warm-600'
                                : 'bg-white text-warm-500 border-warm-200 hover:bg-warm-50 hover:text-warm-700'}`}
                    >
                        {preset === 100 ? 'Done' : `${preset}%`}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default BarProgression;
